import {
  decryptVaultArtifact,
  encryptVaultArtifact,
  type EncryptedVaultArtifactV1,
  type VaultArtifactKind,
  type VaultManifestV1,
  type VaultSession,
} from "@rcprofessionale/vault-core";

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const SHA256_PATTERN = /^[0-9a-f]{64}$/u;

export const VAULT_BUCKET = "vault-artifacts";
export const MAX_ENCRYPTED_ARTIFACT_BYTES = 25 * 1024 * 1024;
export const REQUIRED_ARTIFACT_KINDS = [
  "pdf",
  "editable_snapshot",
  "search_index",
] as const satisfies readonly VaultArtifactKind[];

export interface PlaintextVaultArtifact {
  readonly mimeType: string;
  readonly plaintext: Uint8Array<ArrayBuffer>;
}

export interface PlaintextVaultArtifactSet {
  readonly pdf: PlaintextVaultArtifact;
  readonly editable_snapshot: PlaintextVaultArtifact;
  readonly search_index: PlaintextVaultArtifact;
}

export interface DecryptedVaultArtifactSet {
  readonly pdf: Uint8Array<ArrayBuffer>;
  readonly editable_snapshot: Uint8Array<ArrayBuffer>;
  readonly search_index: Uint8Array<ArrayBuffer>;
}

export interface VaultDocumentIdentity {
  readonly id: string;
  readonly vaultId: string;
  readonly userId: string;
  readonly calculationPublicId?: string;
}

export interface VaultRevisionIdentity extends VaultDocumentIdentity {
  readonly revisionId: string;
  readonly revision: number;
}

export interface VaultArtifactMetadata {
  readonly revisionId: string;
  readonly documentId: string;
  readonly vaultId: string;
  readonly userId: string;
  readonly revision: number;
  readonly artifactKind: VaultArtifactKind;
  readonly storageObjectPath: string;
  readonly encryptedByteLength: number;
  readonly ciphertextSha256: string;
  readonly cryptoFormatVersion: 1;
}

export interface VaultReadyRevisionHeader {
  readonly documentId: string;
  readonly revision: number;
  readonly createdAt: string;
}

export interface VaultPersistenceRemote {
  createVaultManifest(userId: string, manifest: VaultManifestV1): Promise<void>;
  getVaultManifest(): Promise<VaultManifestV1 | null>;
  updateVaultManifest(manifest: VaultManifestV1): Promise<void>;
  createDocument(identity: VaultDocumentIdentity): Promise<void>;
  deleteDocument(documentId: string): Promise<void>;
  createPendingRevision(identity: VaultRevisionIdentity): Promise<void>;
  deletePendingRevision(revisionId: string): Promise<void>;
  uploadEncryptedObject(path: string, payload: Uint8Array<ArrayBuffer>): Promise<void>;
  deleteEncryptedObjects(paths: readonly string[]): Promise<void>;
  insertArtifactMetadata(rows: readonly VaultArtifactMetadata[]): Promise<void>;
  markRevisionReady(revisionId: string): Promise<void>;
  listLatestReadyRevisions(): Promise<readonly VaultReadyRevisionHeader[]>;
  listArtifactMetadata(documentId: string, revision: number): Promise<readonly VaultArtifactMetadata[]>;
  downloadEncryptedObject(path: string): Promise<Uint8Array<ArrayBuffer>>;
}

export interface SaveVaultRevisionInput {
  readonly userId: string;
  readonly session: VaultSession;
  readonly documentId: string;
  readonly revision: number;
  readonly artifacts: PlaintextVaultArtifactSet;
  readonly createdAt?: string;
}

export interface SaveNewVaultDocumentInput {
  readonly userId: string;
  readonly session: VaultSession;
  readonly artifacts: PlaintextVaultArtifactSet;
  readonly documentId?: string;
  readonly calculationPublicId?: string;
  readonly createdAt?: string;
}

export interface SavedVaultRevision {
  readonly documentId: string;
  readonly revisionId: string;
  readonly revision: number;
  readonly storageObjectPaths: readonly string[];
}

export class VaultPersistenceError extends Error {
  readonly phase: string;
  readonly cleanupPending: boolean;
  readonly recoveryPaths: readonly string[];
  override readonly cause: unknown;
  readonly cleanupCause: unknown;

  constructor(
    message: string,
    options: {
      readonly phase: string;
      readonly cleanupPending: boolean;
      readonly recoveryPaths: readonly string[];
      readonly cause: unknown;
      readonly cleanupCause?: unknown;
    },
  ) {
    super(message);
    this.name = "VaultPersistenceError";
    this.phase = options.phase;
    this.cleanupPending = options.cleanupPending;
    this.recoveryPaths = [...options.recoveryPaths];
    this.cause = options.cause;
    this.cleanupCause = options.cleanupCause;
  }
}

function assertUuid(value: string, label: string): void {
  if (!UUID_PATTERN.test(value)) throw new Error(`${label}: UUID non valido`);
}

function assertRevision(revision: number): void {
  if (!Number.isSafeInteger(revision) || revision < 1 || revision > 1_000_000) {
    throw new Error("Numero di revisione non valido");
  }
}

function assertRequiredKinds<T extends { readonly artifactKind: VaultArtifactKind }>(
  artifacts: readonly T[],
): void {
  if (artifacts.length !== REQUIRED_ARTIFACT_KINDS.length) {
    throw new Error("La revisione deve contenere esattamente tre artefatti cifrati");
  }
  const kinds = new Set(artifacts.map((artifact) => artifact.artifactKind));
  for (const kind of REQUIRED_ARTIFACT_KINDS) {
    if (!kinds.has(kind)) throw new Error(`Artefatto cifrato mancante: ${kind}`);
  }
}

function envelopeIsValid(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const envelope = value as Record<string, unknown>;
  return envelope.algorithm === "AES-256-GCM"
    && typeof envelope.iv === "string"
    && /^[A-Za-z0-9_-]{16}$/u.test(envelope.iv)
    && typeof envelope.ciphertext === "string"
    && /^[A-Za-z0-9_-]+$/u.test(envelope.ciphertext);
}

export function parseEncryptedVaultArtifact(
  payload: Uint8Array<ArrayBuffer>,
): EncryptedVaultArtifactV1 {
  if (payload.byteLength === 0 || payload.byteLength > MAX_ENCRYPTED_ARTIFACT_BYTES) {
    throw new Error("Dimensione envelope cifrato non valida");
  }
  let value: unknown;
  try {
    value = JSON.parse(decoder.decode(payload));
  } catch {
    throw new Error("Envelope cifrato JSON non valido");
  }
  if (typeof value !== "object" || value === null) {
    throw new Error("Envelope cifrato non valido");
  }
  const artifact = value as Record<string, unknown>;
  if (
    artifact.format !== "preventivonow-vault-artifact"
    || artifact.version !== 1
    || typeof artifact.vaultId !== "string"
    || !UUID_PATTERN.test(artifact.vaultId)
    || typeof artifact.documentId !== "string"
    || !UUID_PATTERN.test(artifact.documentId)
    || !REQUIRED_ARTIFACT_KINDS.includes(artifact.artifactKind as VaultArtifactKind)
    || typeof artifact.revision !== "number"
    || !Number.isSafeInteger(artifact.revision)
    || artifact.revision < 1
    || typeof artifact.mimeType !== "string"
    || artifact.mimeType.trim().length === 0
    || typeof artifact.createdAt !== "string"
    || typeof artifact.plaintextByteLength !== "number"
    || !Number.isSafeInteger(artifact.plaintextByteLength)
    || artifact.plaintextByteLength < 0
    || !envelopeIsValid(artifact.encryptedDek)
    || !envelopeIsValid(artifact.payload)
    || typeof artifact.ciphertextSha256 !== "string"
    || !SHA256_PATTERN.test(artifact.ciphertextSha256)
  ) {
    throw new Error("Campi envelope cifrato non validi");
  }
  return value as EncryptedVaultArtifactV1;
}

export function serializeEncryptedVaultArtifact(
  artifact: EncryptedVaultArtifactV1,
): Uint8Array<ArrayBuffer> {
  const payload = encoder.encode(JSON.stringify(artifact));
  if (payload.byteLength > MAX_ENCRYPTED_ARTIFACT_BYTES) {
    throw new Error("Envelope cifrato oltre il limite del bucket");
  }
  return payload;
}

export function vaultObjectPath(
  userId: string,
  vaultId: string,
  documentId: string,
  revision: number,
  artifactKind: VaultArtifactKind,
): string {
  assertUuid(userId, "Utente");
  assertUuid(vaultId, "Vault");
  assertUuid(documentId, "Documento");
  assertRevision(revision);
  if (!REQUIRED_ARTIFACT_KINDS.includes(artifactKind)) {
    throw new Error("Tipo artefatto non valido");
  }
  return `${userId}/${vaultId}/${documentId}/${revision}/${artifactKind}.vault`;
}

interface PreparedArtifact {
  readonly artifact: EncryptedVaultArtifactV1;
  readonly payload: Uint8Array<ArrayBuffer>;
  readonly metadata: VaultArtifactMetadata;
}

async function prepareArtifacts(
  identity: VaultRevisionIdentity,
  session: VaultSession,
  inputs: PlaintextVaultArtifactSet,
  createdAt?: string,
): Promise<readonly PreparedArtifact[]> {
  const encrypted = await Promise.all(REQUIRED_ARTIFACT_KINDS.map(async (artifactKind) => {
    const input = inputs[artifactKind];
    const artifact = await encryptVaultArtifact(session, {
      documentId: identity.id,
      artifactKind,
      revision: identity.revision,
      mimeType: input.mimeType,
      plaintext: input.plaintext,
      ...(createdAt === undefined ? {} : { createdAt }),
    });
    const payload = serializeEncryptedVaultArtifact(artifact);
    const storageObjectPath = vaultObjectPath(
      identity.userId,
      identity.vaultId,
      identity.id,
      identity.revision,
      artifactKind,
    );
    return {
      artifact,
      payload,
      metadata: {
        revisionId: identity.revisionId,
        documentId: identity.id,
        vaultId: identity.vaultId,
        userId: identity.userId,
        revision: identity.revision,
        artifactKind,
        storageObjectPath,
        encryptedByteLength: payload.byteLength,
        ciphertextSha256: artifact.ciphertextSha256,
        cryptoFormatVersion: 1 as const,
      },
    };
  }));
  return encrypted;
}

async function compensateFailedSave(
  remote: VaultPersistenceRemote,
  identity: VaultRevisionIdentity,
  uploadedPaths: readonly string[],
  deleteDocument: boolean,
): Promise<{ readonly cleanupPending: boolean; readonly cleanupCause?: unknown }> {
  try {
    if (uploadedPaths.length > 0) await remote.deleteEncryptedObjects(uploadedPaths);
  } catch (cleanupCause) {
    return { cleanupPending: true, cleanupCause };
  }

  try {
    await remote.deletePendingRevision(identity.revisionId);
    if (deleteDocument) await remote.deleteDocument(identity.id);
    return { cleanupPending: false };
  } catch (cleanupCause) {
    return { cleanupPending: true, cleanupCause };
  }
}

async function prepareOrCompensate(
  remote: VaultPersistenceRemote,
  identity: VaultRevisionIdentity,
  session: VaultSession,
  artifacts: PlaintextVaultArtifactSet,
  createdAt: string | undefined,
  deleteDocumentOnFailure: boolean,
): Promise<readonly PreparedArtifact[]> {
  try {
    return await prepareArtifacts(identity, session, artifacts, createdAt);
  } catch (cause) {
    const compensation = await compensateFailedSave(
      remote,
      identity,
      [],
      deleteDocumentOnFailure,
    );
    throw new VaultPersistenceError(
      compensation.cleanupPending
        ? "Cifratura fallita; pulizia automatica incompleta"
        : "Cifratura fallita; revisione incompleta rimossa",
      {
        phase: "encrypt",
        cleanupPending: compensation.cleanupPending,
        recoveryPaths: [],
        cause,
        ...(compensation.cleanupCause === undefined
          ? {}
          : { cleanupCause: compensation.cleanupCause }),
      },
    );
  }
}

async function persistPreparedRevision(
  remote: VaultPersistenceRemote,
  identity: VaultRevisionIdentity,
  prepared: readonly PreparedArtifact[],
  deleteDocumentOnFailure: boolean,
): Promise<SavedVaultRevision> {
  const uploadedPaths: string[] = [];
  let phase = "upload";
  try {
    const results = await Promise.allSettled(prepared.map(async ({ metadata, payload }) => {
      await remote.uploadEncryptedObject(metadata.storageObjectPath, payload);
      uploadedPaths.push(metadata.storageObjectPath);
    }));
    const rejected = results.find((result) => result.status === "rejected");
    if (rejected?.status === "rejected") throw rejected.reason;

    phase = "metadata";
    await remote.insertArtifactMetadata(prepared.map(({ metadata }) => metadata));
    phase = "finalize";
    await remote.markRevisionReady(identity.revisionId);
    return {
      documentId: identity.id,
      revisionId: identity.revisionId,
      revision: identity.revision,
      storageObjectPaths: prepared.map(({ metadata }) => metadata.storageObjectPath),
    };
  } catch (cause) {
    const compensation = await compensateFailedSave(
      remote,
      identity,
      uploadedPaths,
      deleteDocumentOnFailure,
    );
    throw new VaultPersistenceError(
      compensation.cleanupPending
        ? "Salvataggio fallito; pulizia automatica incompleta"
        : "Salvataggio fallito; modifiche incomplete rimosse",
      {
        phase,
        cleanupPending: compensation.cleanupPending,
        recoveryPaths: uploadedPaths,
        cause,
        ...(compensation.cleanupCause === undefined
          ? {}
          : { cleanupCause: compensation.cleanupCause }),
      },
    );
  }
}

export async function saveNewVaultDocument(
  remote: VaultPersistenceRemote,
  input: SaveNewVaultDocumentInput,
): Promise<SavedVaultRevision> {
  assertUuid(input.userId, "Utente");
  assertUuid(input.session.vaultId, "Vault");
  const documentId = input.documentId ?? crypto.randomUUID();
  assertUuid(documentId, "Documento");
  if (input.calculationPublicId !== undefined) {
    assertUuid(input.calculationPublicId, "Calcolo");
  }
  const identity: VaultRevisionIdentity = {
    id: documentId,
    vaultId: input.session.vaultId,
    userId: input.userId,
    ...(input.calculationPublicId === undefined
      ? {}
      : { calculationPublicId: input.calculationPublicId }),
    revisionId: crypto.randomUUID(),
    revision: 1,
  };

  await remote.createDocument(identity);
  try {
    await remote.createPendingRevision(identity);
  } catch (cause) {
    try {
      await remote.deleteDocument(identity.id);
    } catch {
      // The empty document remains visible as technical metadata for recovery.
    }
    throw new VaultPersistenceError("Creazione revisione fallita", {
      phase: "revision",
      cleanupPending: true,
      recoveryPaths: [],
      cause,
    });
  }
  const prepared = await prepareOrCompensate(
    remote,
    identity,
    input.session,
    input.artifacts,
    input.createdAt,
    true,
  );
  return persistPreparedRevision(remote, identity, prepared, true);
}

export async function saveVaultRevision(
  remote: VaultPersistenceRemote,
  input: SaveVaultRevisionInput,
): Promise<SavedVaultRevision> {
  assertUuid(input.userId, "Utente");
  assertUuid(input.session.vaultId, "Vault");
  assertUuid(input.documentId, "Documento");
  assertRevision(input.revision);
  const identity: VaultRevisionIdentity = {
    id: input.documentId,
    vaultId: input.session.vaultId,
    userId: input.userId,
    revisionId: crypto.randomUUID(),
    revision: input.revision,
  };
  await remote.createPendingRevision(identity);
  const prepared = await prepareOrCompensate(
    remote,
    identity,
    input.session,
    input.artifacts,
    input.createdAt,
    false,
  );
  return persistPreparedRevision(remote, identity, prepared, false);
}

export async function loadEncryptedVaultRevision(
  remote: VaultPersistenceRemote,
  documentId: string,
  revision: number,
): Promise<readonly EncryptedVaultArtifactV1[]> {
  assertUuid(documentId, "Documento");
  assertRevision(revision);
  const metadata = await remote.listArtifactMetadata(documentId, revision);
  assertRequiredKinds(metadata);

  const artifacts = await Promise.all(metadata.map(async (row) => {
    const payload = await remote.downloadEncryptedObject(row.storageObjectPath);
    if (payload.byteLength !== row.encryptedByteLength) {
      throw new Error(`Dimensione oggetto non coincidente: ${row.artifactKind}`);
    }
    const artifact = parseEncryptedVaultArtifact(payload);
    if (
      artifact.documentId !== documentId
      || artifact.revision !== revision
      || artifact.vaultId !== row.vaultId
      || artifact.artifactKind !== row.artifactKind
      || artifact.ciphertextSha256 !== row.ciphertextSha256
      || row.cryptoFormatVersion !== artifact.version
    ) {
      throw new Error(`Metadati e envelope non coincidono: ${row.artifactKind}`);
    }
    return artifact;
  }));
  assertRequiredKinds(artifacts);
  return artifacts;
}

export async function loadAndDecryptVaultRevision(
  remote: VaultPersistenceRemote,
  session: VaultSession,
  documentId: string,
  revision: number,
): Promise<DecryptedVaultArtifactSet> {
  const encrypted = await loadEncryptedVaultRevision(remote, documentId, revision);
  const decrypted = new Map<VaultArtifactKind, Uint8Array<ArrayBuffer>>();
  try {
    for (const artifact of encrypted) {
      decrypted.set(artifact.artifactKind, await decryptVaultArtifact(session, artifact));
    }
    const pdf = decrypted.get("pdf");
    const editableSnapshot = decrypted.get("editable_snapshot");
    const searchIndex = decrypted.get("search_index");
    if (pdf === undefined || editableSnapshot === undefined || searchIndex === undefined) {
      throw new Error("Revisione decifrata incompleta");
    }
    return { pdf, editable_snapshot: editableSnapshot, search_index: searchIndex };
  } catch (error) {
    for (const value of decrypted.values()) value.fill(0);
    throw error;
  }
}
